import {
  type Condition,
  ConditionOperatorEnum,
  SortOrder,
} from '@repo/api-gw-sdk';

import { collectValueConditions } from '@/types/advanceFilter';

export const convertQuery = (
  rootCondition: Condition | undefined
): Record<string, unknown> | undefined => {
  if (!rootCondition?.conditions?.length) {
    return;
  }

  const conditions = collectValueConditions(rootCondition);
  return conditions.reduce<Record<string, unknown>>((acc, condition) => {
    acc[condition.property!] = {
      [convertOperator(condition.operator)]: condition.value,
    };
    return acc;
  }, {});
};

// Maps ConditionOperatorEnum values to the TypeScript property names used by the
// SDK filter classes (e.g. CloudProviderFilters, FreeTextFilters). These are the
// `name` fields in each class's attributeTypeMap, NOT the wire-format `baseName`.
// ObjectSerializer.serialize() handles the name→baseName translation when building
// the HTTP body (e.g. '_in' becomes 'in' on the wire for CloudProviderFilters).
// The mock's applyTokenFilters reads the already-serialized body, so it checks
// the baseName keys ('in', 'notIn', 'contains', ...) — not these intermediate names.
const operatorMap: Partial<Record<ConditionOperatorEnum, string>> = {
  [ConditionOperatorEnum.In]: '_in',
  [ConditionOperatorEnum.NotIn]: 'notIn',
  [ConditionOperatorEnum.Contains]: 'contains',
  [ConditionOperatorEnum.NotContains]: 'notContains',
  [ConditionOperatorEnum.ContainsAnyOf]: 'containsAnyOf',
  [ConditionOperatorEnum.ContainsNoneOf]: 'containsNoneOf',
};

const convertOperator = (operator: string): string =>
  (operator in operatorMap
    ? operatorMap[operator as ConditionOperatorEnum]
    : undefined) ?? operator;

export const convertSort = <T extends string>(
  sort: string | undefined,
  fieldMap: Record<string, T>
): { field: T; order: SortOrder }[] | undefined => {
  if (!sort) {
    return undefined;
  }
  const [fieldStr, orderStr] = sort.split(':');
  const field = fieldMap[fieldStr];
  if (field === undefined) {
    return undefined;
  }
  const order =
    orderStr?.toLowerCase() === 'desc' ? SortOrder.Desc : SortOrder.Asc;
  return [{ field, order }];
};
